// Libraries
import React, {forwardRef} from 'react';

// Supermove
import {DataLoader, Form, Space, Styled} from '@supermove/components';
import {DocumentNode, gql, useQuery} from '@supermove/graphql';
import {
  DocumentItemInputSavePaymentType,
  DocumentModel,
  OrganizationModel,
  PaymentMethodModel,
  ProjectModel,
} from '@supermove/models';

// App
import FieldInput from '@shared/design/components/Field/FieldInput';
import Text from '@shared/design/components/Text';
import DocumentV2SaveClientCreditCard, {
  DocumentV2SaveClientCardHandle,
} from '@shared/modules/Document/components/DocumentV2SaveClientCreditCard';

const LoadingIndicator = Styled.Loading`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const DocumentV2SavePaymentInputNoPaymentMethod = ({
  organization,
}: {
  organization?: OrganizationModel;
}) => {
  return (
    <React.Fragment>
      <Text.Body>
        Please contact {organization?.name || 'the office'} to make your payment.
      </Text.Body>
    </React.Fragment>
  );
};

const DocumentV2SavePaymentInputBodyView = ({
  paymentMethodName,
  cardSummaryName,
}: {
  paymentMethodName: string;
  cardSummaryName: string;
}) => {
  return (
    <React.Fragment>
      <Text.Subheading>Payment Method</Text.Subheading>
      <Space height={8} />
      <Row>
        <Text.Label>{paymentMethodName}</Text.Label>
        <Space width={32} />
        <Text.Body>{cardSummaryName}</Text.Body>
      </Row>
      <Space height={8} />
      <Text.Micro>Your card will not be charged at this time.</Text.Micro>
    </React.Fragment>
  );
};

const DocumentV2SavePaymentInputBodyEdit = forwardRef<
  DocumentV2SaveClientCardHandle,
  {
    form: Form;
    field: string;
    isRequired: boolean;
    paymentMethod: PaymentMethodModel;
    project: ProjectModel;
    input: DocumentItemInputSavePaymentType['input'];
  }
>(({form, field, isRequired, paymentMethod, project, input}, ref) => {
  if (paymentMethod.kind === 'PAYENGINE_CREDIT_CARD') {
    return (
      <React.Fragment>
        <Text.Subheading>Credit Card Information</Text.Subheading>
        <Space height={8} />
        <FieldInput.LabelText isResponsive isRequired={isRequired}>
          Payment Method
        </FieldInput.LabelText>
        <Space height={4} />
        <DocumentV2SaveClientCreditCard
          ref={ref}
          project={project}
          initialSelectedCreditCardId={input.value.payengineCreditCardId}
          allowSelectExistingPaymentMethods={input.metadata.allowSelectExistingPaymentMethods}
          onSelect={(payengineCreditCard) => {
            form.setFieldValue(
              `${field}.input.value.payengineCreditCardId`,
              payengineCreditCard?.id || '',
            );
            form.setFieldValue(`${field}.input.value.paymentMethodName`, paymentMethod.name || '');
            form.setFieldValue(
              `${field}.input.value.cardSummaryName`,
              payengineCreditCard?.cardSummaryName || '',
            );
          }}
        />
        <Space height={8} />
        <Text.Micro>Your card will not be charged at this time.</Text.Micro>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Text.Subheading>Payment Method</Text.Subheading>
      <Space height={8} />
      <Text.Body>Please contact {project.organization.name} to make your payment.</Text.Body>
    </React.Fragment>
  );
});

type DocumentV2SavePaymentInputEditProps = {
  input: DocumentItemInputSavePaymentType['input'];
  form: Form;
  field: string;
  isEditable: boolean;
  document: DocumentModel;
  isRequired: boolean;
};

type DocumentV2SavePaymentInputEditComponent = React.ForwardRefExoticComponent<
  DocumentV2SavePaymentInputEditProps & React.RefAttributes<DocumentV2SaveClientCardHandle>
> & {
  query: DocumentNode;
  fragment: DocumentNode;
};

const DocumentV2SavePaymentInputEdit = forwardRef<
  DocumentV2SaveClientCardHandle,
  DocumentV2SavePaymentInputEditProps
>(({input, form, field, isEditable, document, isRequired}, ref) => {
  const {paymentMethodIds} = input.metadata;
  const paymentMethodId = paymentMethodIds.length > 0 ? paymentMethodIds[0] : null;

  const project = document?.job ? document.job.project : document?.invoice.project;

  const {data, loading} = useQuery<{
    project: ProjectModel;
  }>(DocumentV2SavePaymentInputEdit.query, {
    variables: {projectUuid: project.uuid, paymentMethodId},
    skip: !paymentMethodId || !project,
  });

  if (!paymentMethodId) {
    return <DocumentV2SavePaymentInputNoPaymentMethod organization={project.organization} />;
  }

  if (!isEditable) {
    return (
      <DocumentV2SavePaymentInputBodyView
        paymentMethodName={input.value.paymentMethodName}
        cardSummaryName={input.value.cardSummaryName}
      />
    );
  }

  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) =>
        loadedData?.project.organization.paymentMethod ? (
          <DocumentV2SavePaymentInputBodyEdit
            ref={ref}
            form={form}
            field={field}
            isRequired={isRequired}
            paymentMethod={loadedData.project.organization.paymentMethod}
            project={project}
            input={input}
          />
        ) : (
          <DocumentV2SavePaymentInputNoPaymentMethod organization={project.organization} />
        )
      }
    </DataLoader>
  );
}) as DocumentV2SavePaymentInputEditComponent;

DocumentV2SavePaymentInputEdit.fragment = gql`
  ${DocumentV2SaveClientCreditCard.fragment}

  fragment DocumentV2SavePaymentInputEdit_Project on Project {
    id
    uuid
    client {
      id
    }
    organization {
      id
      name
    }
  }

  fragment DocumentV2SavePaymentInputEdit_Document on Document {
    id
    job {
      id
      project {
        id
        ...DocumentV2SavePaymentInputEdit_Project
      }
    }
    invoice {
      id
      project {
        id
        ...DocumentV2SavePaymentInputEdit_Project
      }
    }
    ...DocumentV2SaveClientCreditCard_Document
  }
`;

// Payment items rely on payment method id, so we need a separate query
DocumentV2SavePaymentInputEdit.query = gql`
  query DocumentV2SavePaymentInputEdit($projectUuid: String!, $paymentMethodId: Int!) {
    project(uuid: $projectUuid) {
      id
      organization {
        id
        paymentMethod(paymentMethodId: $paymentMethodId) {
          id
          kind
          name
        }
      }
    }
  }
`;

export default DocumentV2SavePaymentInputEdit;
